import React from 'react'

import Divider from '@mui/material/Divider'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'

import { AddBox, ChevronRight } from '@mxenabled/mx-icons'

import { useAccountStore, useGlobalCopyStore, useGoalStore } from '../../../common'
import { Account, Goal, RetirementGoalAccount } from '../../../core'

import { AccountSelectionDrawer } from '../AccountSelectionDrawer'
import { AccountAction } from './AccountAction'

interface AddRetirementAccountActionProps {
  goal: Goal
}

export const AddRetirementAccountAction = ({ goal }: AddRetirementAccountActionProps) => {
  const { goals: copy } = useGlobalCopyStore()
  const { visibleRetirementAccounts: retirementAccounts } = useAccountStore()
  const {
    addRetirementGoalAccount,
    newRetirementGoalAccounts,
    retirementGoalAccounts,
    selectedGoal,
    setAlert,
    setNewRetirementGoalAccounts,
    setSelectedGoal,
  } = useGoalStore()

  const [isOpen, setIsOpen] = React.useState(false)

  const isNew = !goal.guid
  const accounts = retirementAccounts.filter((a) =>
    isNew
      ? newRetirementGoalAccounts.find((rga) => rga.account_guid === a.guid)
      : retirementGoalAccounts.find((rga) => rga.account_guid === a.guid),
  )

  const availableAccounts = retirementAccounts.filter(
    (a) => !retirementGoalAccounts.find((rga) => rga.account_guid === a.guid),
  )

  const handleSave = async (accounts: Account[]) => {
    const retirementAccounts = accounts.map((account) => ({
      account_guid: account.guid,
      retirement_goal_guid: goal.guid,
    })) as RetirementGoalAccount[]

    if (isNew) {
      setNewRetirementGoalAccounts(retirementAccounts)
    } else {
      for (const account of retirementAccounts) {
        await addRetirementGoalAccount(account)
        setAlert(copy.alert_account_added)
      }
    }
    setSelectedGoal({
      ...selectedGoal!,
      current_amount: accounts.reduce((sum, account) => sum + (account.balance || 0), 0),
    })
    setIsOpen(false)
  }

  return (
    <Stack>
      {accounts.map((account) => (
        <Stack key={account.guid}>
          <AccountAction account={account} />
          <Divider />
        </Stack>
      ))}
      <ListItem disableGutters={true} disablePadding={true} sx={{ bgcolor: 'background.paper' }}>
        <ListItemButton onClick={() => setIsOpen(true)}>
          <ListItemText>
            <Stack alignItems="center" color="primary.main" direction="row" gap={8}>
              <AddBox filled={true} />
              {copy.details_add_account}
            </Stack>
          </ListItemText>
          <ChevronRight color="primary" />
        </ListItemButton>
      </ListItem>

      <AccountSelectionDrawer
        accounts={availableAccounts}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onSave={handleSave}
        title={copy.add_select_accounts}
      />
    </Stack>
  )
}
